import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  Legend,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import GlobalFilters from "@/components/CollapsibleFiltersPanel";
import ActiveFiltersChips from "@/components/ActiveFiltersChips";
import Ranking3DExtended from "@/components/Ranking3DExtended";
import { useFilters } from "@/hooks/useFilters";
import reports from "@/api/reports";
import { TX_COLORS, colorFor } from "@/lib/reportColors";

const LIMITS = [5, 10, 20, 50];

export default function Ranking() {
  const { t } = useTranslation();
  const nav = useNavigate();
  const { filters, setFilters, reset, payload } = useFilters();
  const [limit, setLimit] = useState(20);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);

  // Stringify the filter payload so referential changes don't trigger needless
  // refetches (intentional — payload identity changes on every render).
  const payloadKey = useMemo(() => JSON.stringify(payload), [payload]);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    reports
      .ranking(payload, limit)
      .then((r) => alive && setItems(r.data?.items || []))
      .finally(() => alive && setLoading(false));
    return () => {
      alive = false;
    };
    // payload tracked indirectly via payloadKey.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [payloadKey, limit]);

  const types = useMemo(() => {
    const s = new Set();
    items.forEach((it) => Object.keys(it.by_type || {}).forEach((k) => s.add(k)));
    return Array.from(s).sort();
  }, [items]);

  const data = useMemo(
    () =>
      items.map((it) => ({
        ...it,
        ...(it.by_type || {}),
      })),
    [items]
  );

  return (
    <div className="space-y-6" data-testid="ranking-page">
      <div>
        <h1 className="text-3xl font-semibold tracking-tight" data-testid="ranking-title">
          {t("ranking.title")}
        </h1>
        <p className="text-sm text-muted-foreground">{t("ranking.subtitle")}</p>
      </div>

      <GlobalFilters filters={filters} onChange={setFilters} onReset={reset} />

      <ActiveFiltersChips
        filters={filters}
        onClear={(key, next) => {
          if (key === "__all__") {
            setFilters({
              employee_ids: [],
              department_ids: [],
              transaction_types: [],
              day_of_week: [],
            });
            return;
          }
          setFilters({ [key]: next });
        }}
        testId="ranking-drilldown-chips"
      />

      {/* Hero — Extended 3D ranking visualization */}
      <Ranking3DExtended
        payload={payload}
        defaultTop={12}
        testId="ranking-3d-extended-page"
        selectedEmployeeId={(filters.employee_ids || [])[0] || null}
        onEmployeeClick={(item) => {
          const id = String(item.employee_id || "");
          const already = (filters.employee_ids || []).includes(id);
          setFilters({ employee_ids: already ? [] : [id] });
        }}
      />

      <div className="flex items-end gap-3">
        <div className="space-y-1.5">
          <Label className="text-xs">{t("ranking.limit")}</Label>
          <Select value={String(limit)} onValueChange={(v) => setLimit(Number(v))}>
            <SelectTrigger data-testid="ranking-limit-select" className="h-9 w-28">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {LIMITS.map((l) => (
                <SelectItem key={l} value={String(l)} data-testid={`ranking-limit-${l}`}>
                  Top {l}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <span className="text-xs text-muted-foreground">{t("ranking.clickHint")}</span>
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="text-base">
            {t("ranking.title")} · {t("ranking.stacked")}
          </CardTitle>
        </CardHeader>
        <CardContent>
          {data.length === 0 ? (
            <div className="py-16 text-center text-sm text-muted-foreground" data-testid="ranking-empty">
              {loading ? t("common.loading") : t("common.no_data")}
            </div>
          ) : (
            <ResponsiveContainer width="100%" height={Math.max(400, data.length * 28)}>
              <BarChart
                data={data}
                layout="vertical"
                margin={{ left: 80, right: 24 }}
              >
                <CartesianGrid strokeDasharray="3 3" opacity={0.2} />
                <XAxis type="number" />
                <YAxis
                  type="category"
                  dataKey="employee_name"
                  width={140}
                  tick={{ fontSize: 11, cursor: "pointer" }}
                  onClick={(e) => {
                    // Recharts passes the tick payload as { value, index, ... }
                    const idx = e?.index;
                    const emp = typeof idx === "number" ? data[idx] : null;
                    if (emp?.employee_id) {
                      nav(`/reports/comparativo?compare=${emp.employee_id}`);
                    }
                  }}
                />
                <Tooltip
                  cursor={{ fill: "hsl(var(--accent) / 0.15)" }}
                  contentStyle={{
                    background: "hsl(var(--popover))",
                    border: "1px solid hsl(var(--border))",
                  }}
                  formatter={(v, n) => [v.toLocaleString(), n]}
                />
                <Legend />
                {types.map((tt, i) => (
                  <Bar
                    key={tt}
                    dataKey={tt}
                    stackId="a"
                    fill={TX_COLORS[tt] || colorFor(tt, i)}
                    cursor="pointer"
                    onClick={(payload) => {
                      const emp = payload?.payload;
                      if (emp?.employee_id) {
                        nav(`/reports/comparativo?compare=${emp.employee_id}`);
                      }
                    }}
                    data-testid={`ranking-bar-${tt.replace(/\s+/g, "_")}`}
                  />
                ))}
              </BarChart>
            </ResponsiveContainer>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
